// @mui
import Button from '@mui/material/Button'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
// hooks
import { useBoolean } from 'src/hooks/use-boolean'
// components
import Iconify from 'src/components/iconify'
import Label from 'src/components/label'
import { ConfirmDialog } from 'src/components/custom-dialog'
import { masks } from 'src/utils/masks'

import { ICardItem } from 'src/types/card'
import UserCreateEditForm from '../user-create-edit-view'

// ----------------------------------------------------------------------

type Props = {
  selected: boolean
  onEditRow: VoidFunction
  row: ICardItem
  refetch: VoidFunction
  onDeleteRow: VoidFunction
}

export default function UserTableRow({ row, selected, onEditRow, onDeleteRow, refetch }: Props) {
  const { card, name, event, balance = 0, totalTransactions = 0 } = row

  const confirm = useBoolean()
  const editName = useBoolean()

  const getBalanceColor = () => {
    if (balance > 0) return 'success.main'
    if (balance < 0) return 'error.main'
    return 'text.secondary'
  }
  const balanceColor = getBalanceColor()

  return (
    <>
      <TableRow hover selected={selected}>
        {/* Cartão (nome + número) */}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.25,
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Iconify icon='solar:card-bold' width={18} />
            </Box>
            <Stack spacing={0.25}>
              <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>
                {name || 'Sem nome'}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                {card}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>

        {/* Evento */}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant='body2' sx={{ fontSize: 13 }}>
            {event?.name || 'Sem evento associado'}
          </Typography>
        </TableCell>

        {/* Saldo */}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant='body2' fontWeight={700} sx={{ fontSize: 13, color: balanceColor }}>
            {masks.currencyAllPlatforms(balance)}
          </Typography>
        </TableCell>

        {/* Movimentações */}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Label variant='soft' color={totalTransactions > 0 ? 'info' : 'default'}>
            {totalTransactions} mov.
          </Label>
        </TableCell>

        {/* Ações */}
        <TableCell align='right' sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Tooltip title='Ver movimentações' placement='top' arrow>
            <IconButton color='primary' onClick={onEditRow}>
              <Iconify icon='solar:eye-bold' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Editar nome' placement='top' arrow>
            <IconButton onClick={editName.onTrue}>
              <Iconify icon='solar:pen-bold' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Deletar' placement='top' arrow>
            <IconButton onClick={() => confirm.onTrue()}>
              <Iconify color='red' icon='solar:trash-bin-trash-bold' />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title='Deletar cartão'
        content='Tem certeza que quer deletar este cartão?'
        action={
          <Button variant='contained' color='error' onClick={onDeleteRow}>
            Deletar
          </Button>
        }
      />

      <UserCreateEditForm
        open={editName.value}
        onClose={editName.onFalse}
        onRefleshTable={refetch}
        currentUser={row}
      />
    </>
  )
}
